import {
  data,
  redirect,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
  Form,
  Link,
  useLoaderData,
} from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { markConceptStepIncomplete } from '#app/controls/concept-step.ts'
import { markConceptIncomplete } from '#app/controls/concept.ts'
import { markTrainingProgramIncomplete } from '#app/controls/training-program.ts'
import { markUnitIncomplete } from '#app/controls/unit.ts'
import { type Slug, executeQuery } from '#app/utils/cms.server.ts'
import { markConceptStepComplete } from '#app/utils/concept-step.server.ts'
import { markConceptComplete } from '#app/utils/concept.server.ts'
import { requireControlsToBeEnabled } from '#app/utils/controls.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { type LocalizedContent } from '#app/utils/i18n.ts'
import { invariantResponse } from '#app/utils/misc.tsx'
import { Status } from '#app/utils/status.js'
import { markTrainingProgramComplete } from '#app/utils/training-program.server.ts'
import { markUnitComplete } from '#app/utils/unit.server.ts'
import { getActiveEnrollments } from '#app/utils/user.server.ts'

interface TrainingProgram {
  _id: string
  slug: Slug
  title: LocalizedContent
  units: Unit[]
}

interface Unit {
  _id: string
  slug: Slug
  number: number
  title: LocalizedContent
  concepts: Concept[]
}

interface Concept {
  _id: string
  slug: Slug
  number: number
  title: LocalizedContent
  steps: ConceptStep[]
}

interface ConceptStep {
  _id: string
  slug: Slug
  number: number
}

const checkMark = '✔️'
const crossMark = '✖️'
const markCompleteButtonText = 'Mark complete'
const markIncompleteButtonText = 'Mark incomplete'

export async function loader({ params }: LoaderFunctionArgs) {
  requireControlsToBeEnabled()

  const userId = params.id
  if (!userId) {
    return redirect('/controls/training')
  }

  const user = await prisma.user.findUnique({
    where: { id: params.id },
    select: {
      id: true,
      email: true,
      trainingProgramCompletions: {
        select: {
          trainingProgramId: true,
        },
      },
      unitCompletions: {
        select: {
          unitId: true,
        },
      },
      conceptCompletions: {
        select: {
          conceptId: true,
        },
      },
      conceptStepCompletions: {
        select: {
          conceptStepId: true,
        },
      },
    },
  })

  if (!user) {
    return redirect('/controls/training')
  }

  const programCompletions = user.trainingProgramCompletions.map(
    completion => completion.trainingProgramId,
  )

  const unitCompletions = user.unitCompletions.map(
    completion => completion.unitId,
  )

  const conceptCompletions = user.conceptCompletions.map(
    completion => completion.conceptId,
  )

  const conceptStepCompletions = user.conceptStepCompletions.map(
    completion => completion.conceptStepId,
  )

  const enrollments = await getActiveEnrollments(userId)
  const trainingProgramIds = enrollments.map(
    enrollment => enrollment.trainingProgramId,
  )

  const trainingProgramQuery = `*[_type == 'trainingProgram' && _id in ${JSON.stringify(trainingProgramIds)}]{_id, slug, title, units[]->{_id, slug, number, title, concepts[]->{_id, slug, number, title, steps[]->{_id, slug, number}}}}`
  const trainingProgramResult = await executeQuery(trainingProgramQuery)

  if (trainingProgramResult.status !== Status.Success) {
    throw data({}, { status: 500 })
  }

  const trainingPrograms = trainingProgramResult.data as TrainingProgram[]

  return {
    user,
    programCompletions,
    unitCompletions,
    conceptCompletions,
    conceptStepCompletions,
    trainingPrograms,
  }
}

export async function action({ params, request }: ActionFunctionArgs) {
  const userId = params.id
  invariantResponse(typeof userId === 'string', 'User ID must be a string')

  const formData = await request.formData()
  const intent = formData.get('intent')

  switch (intent) {
    case 'markProgramIncomplete': {
      const trainingProgramId = formData.get('trainingProgramId')
      invariantResponse(
        typeof trainingProgramId === 'string',
        'Training program ID must be a string',
      )

      await markTrainingProgramIncomplete(userId, trainingProgramId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markProgramComplete': {
      const trainingProgramId = formData.get('trainingProgramId')
      invariantResponse(
        typeof trainingProgramId === 'string',
        'Training program ID must be a string',
      )

      await markTrainingProgramComplete(userId, trainingProgramId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markUnitComplete': {
      const trainingProgramId = formData.get('trainingProgramId')
      invariantResponse(
        typeof trainingProgramId === 'string',
        'Training program ID must be a string',
      )

      const unitId = formData.get('unitId')
      invariantResponse(typeof unitId === 'string', 'Unit ID must be a string')

      await markUnitComplete(userId, trainingProgramId, unitId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markUnitIncomplete': {
      const unitId = formData.get('unitId')
      invariantResponse(typeof unitId === 'string', 'Unit ID must be a string')

      await markUnitIncomplete(userId, unitId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markConceptComplete': {
      const unitId = formData.get('unitId')
      invariantResponse(typeof unitId === 'string', 'Unit ID must be a string')

      const conceptId = formData.get('conceptId')
      invariantResponse(
        typeof conceptId === 'string',
        'Concept ID must be a string',
      )

      await markConceptComplete(userId, unitId, conceptId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markConceptIncomplete': {
      const conceptId = formData.get('conceptId')
      invariantResponse(
        typeof conceptId === 'string',
        'Concept ID must be a string',
      )

      await markConceptIncomplete(userId, conceptId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markConceptStepComplete': {
      const conceptId = formData.get('conceptId')
      invariantResponse(
        typeof conceptId === 'string',
        'Concept ID must be a string',
      )

      const conceptStepId = formData.get('conceptStepId')
      invariantResponse(
        typeof conceptStepId === 'string',
        'Concept step ID must be a string',
      )

      await markConceptStepComplete(userId, conceptId, conceptStepId)

      return redirect(`/controls/training/${userId}`)
    }
    case 'markConceptStepIncomplete': {
      const conceptStepId = formData.get('conceptStepId')
      invariantResponse(
        typeof conceptStepId === 'string',
        'Concept step ID must be a string',
      )

      await markConceptStepIncomplete(userId, conceptStepId)

      return redirect(`/controls/training/${userId}`)
    }
    default: {
      throw new Response(`${intent} intent is not supported`, {
        status: 400,
      })
    }
  }
}

const TrainingControl = ({
  trainingPrograms,
  programCompletions,
  unitCompletions,
  conceptCompletions,
  conceptStepCompletions,
}: {
  trainingPrograms: TrainingProgram[]
  programCompletions: string[]
  unitCompletions: string[]
  conceptCompletions: string[]
  conceptStepCompletions: string[]
}) => (
  <>
    <h2 className="text-h3">Training</h2>

    {trainingPrograms.map(trainingProgram => {
      let programStatus = crossMark
      let programIntent = 'markProgramComplete'
      let programButtonText = markCompleteButtonText

      const programIsComplete = programCompletions.includes(trainingProgram._id)

      if (programIsComplete) {
        programStatus = checkMark
        programIntent = 'markProgramIncomplete'
        programButtonText = markIncompleteButtonText
      }

      return (
        <div className="mt-4" key={trainingProgram._id}>
          <h3 className="font-bold">
            {programStatus} {trainingProgram.title.en}
          </h3>
          <div className="flex items-center">
            <Form method="POST">
              <input
                type="hidden"
                name="trainingProgramId"
                value={trainingProgram._id}
              />
              <Button
                type="submit"
                name="intent"
                value={programIntent}
                variant="link"
              >
                {programButtonText}
              </Button>
            </Form>
            <Link to={`/training/${trainingProgram.slug.current}`}>
              View training program
            </Link>
          </div>
          <ul className="pl-6">
            {trainingProgram.units.map(unit => {
              let unitStatus = crossMark
              let unitIntent = 'markUnitComplete'
              let unitButtonText = markCompleteButtonText

              const unitIsComplete = unitCompletions.includes(unit._id)

              if (unitIsComplete) {
                unitStatus = checkMark
                unitIntent = 'markUnitIncomplete'
                unitButtonText = markIncompleteButtonText
              }

              return (
                <li key={unit._id}>
                  {unitStatus} Unit {unit.number}: {unit.title.en}
                  <Form method="POST">
                    <input
                      type="hidden"
                      name="trainingProgramId"
                      value={trainingProgram._id}
                    />
                    <input type="hidden" name="unitId" value={unit._id} />
                    <Button
                      type="submit"
                      name="intent"
                      value={unitIntent}
                      variant="link"
                    >
                      {unitButtonText}
                    </Button>
                  </Form>
                  <ul className="pl-6">
                    {unit.concepts.map(concept => {
                      let conceptStatus = crossMark
                      let conceptIntent = 'markConceptComplete'
                      let conceptButtonText = markCompleteButtonText

                      const conceptIsComplete = conceptCompletions.includes(
                        concept._id,
                      )

                      if (conceptIsComplete) {
                        conceptStatus = checkMark
                        conceptIntent = 'markConceptIncomplete'
                        conceptButtonText = markIncompleteButtonText
                      }

                      return (
                        <li key={concept._id}>
                          {conceptStatus}
                          Concept {concept.number}: {concept.title.en}
                          <div>
                            <Form method="POST" className="inline-block">
                              <input
                                type="hidden"
                                name="unitId"
                                value={unit._id}
                              />
                              <input
                                type="hidden"
                                name="conceptId"
                                value={concept._id}
                              />
                              <Button
                                type="submit"
                                name="intent"
                                value={conceptIntent}
                                variant="link"
                              >
                                {conceptButtonText}
                              </Button>
                            </Form>
                            <Link
                              to={`/training/${trainingProgram.slug.current}/${unit.slug.current}/${concept.slug.current}`}
                            >
                              View concept
                            </Link>
                          </div>
                          <ul className="pl-6">
                            {concept.steps &&
                              concept.steps.map(step => {
                                let conceptStepStatus = crossMark
                                let conceptStepIntent =
                                  'markConceptStepComplete'
                                let conceptStepButtonText =
                                  markCompleteButtonText

                                const complete =
                                  conceptStepCompletions.includes(step._id)

                                if (complete) {
                                  conceptStepStatus = checkMark
                                  conceptStepIntent =
                                    'markConceptStepIncomplete'
                                  conceptStepButtonText =
                                    markIncompleteButtonText
                                }

                                return (
                                  <li key={step._id}>
                                    {conceptStepStatus} Step {step.number}
                                    <Form method="POST" className="inline">
                                      <input
                                        type="hidden"
                                        name="conceptId"
                                        value={concept._id}
                                      />
                                      <input
                                        type="hidden"
                                        name="conceptStepId"
                                        value={step._id}
                                      />
                                      <Button
                                        type="submit"
                                        name="intent"
                                        value={conceptStepIntent}
                                        variant="link"
                                      >
                                        {conceptStepButtonText}
                                      </Button>
                                    </Form>
                                    <Link
                                      to={`/training/${trainingProgram.slug.current}/${unit.slug.current}/${concept.slug.current}/${step.slug.current}`}
                                    >
                                      View step
                                    </Link>
                                  </li>
                                )
                              })}
                          </ul>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>
        </div>
      )
    })}
  </>
)

export default function UserTrainingControlRoute() {
  const data = useLoaderData<typeof loader>()
  const {
    user,
    trainingPrograms,
    programCompletions,
    unitCompletions,
    conceptCompletions,
    conceptStepCompletions,
  } = data

  return (
    <>
      <ul className="flex gap-3">
        <li>
          <Link to={'/controls'}>Controls</Link>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            <Link to="/controls/training">Training</Link>
          </span>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">{user.email}</span>
        </li>
      </ul>
      <dl>
        <dt className="mt-6 font-bold">User ID</dt>
        <dd>{user.id}</dd>
        <dt className="mt-6 font-bold">Email</dt>
        <dd>{user.email}</dd>
      </dl>
      <hr className="mt-6" />
      <div className="mt-6">
        <TrainingControl
          trainingPrograms={trainingPrograms}
          programCompletions={programCompletions}
          unitCompletions={unitCompletions}
          conceptCompletions={conceptCompletions}
          conceptStepCompletions={conceptStepCompletions}
        />
      </div>
    </>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    {
      title: `Controls > Training > ${
        data ? data.user.email : 'User'
      } | ${siteTitle}`,
    },
  ]
}
